import classNames from "classnames";
import { ChannelForwardChannelSelector } from "components/ChannelForwardChannelSelector/ChannelForwardChannelSelector";
import { ChatActionCentreSlider } from "components/ChannelinfoSlider/ChannelInfo/ActionCentre/ChatActionCentreSlider";
import { ChatTicketSlider } from "components/ChannelinfoSlider/ChannelInfo/ChatTickets/ChatTicketsSlider";
import { ChannelInfoSlider } from "components/ChannelinfoSlider/ChannelinfoSlider";
import { ChannelPendingActions } from "components/ChannelinfoSlider/ChannelPendingActions";
import { ChannelPinnedItem } from "components/ChannelinfoSlider/ChannelPinnedItem";
import { ChannelMediaSlider } from "components/ChannelMediaSlider/ChannelMediaSlider";
import { ChannelMessageSearch } from "components/ChannelMessageSearch/ChannelMessageSearch";
import { ChannelListFilter } from "components/ChannelSearch/ChannelListFilter";
import Loader from "components/DesignSystem/Loader/loader";
import { MessageInfoSlider } from "components/WhatsappSharedSlider/MessageInfoSlider";
import { WhatsappForwardedSlider } from "components/WhatsappSharedSlider/WhatsappForwardedSlider";
import { BIG, EXPANDED } from "constants/chatBubbleWidths";
import { useExpandedMessageWidth } from "hooks/useExpandedMessageWidth";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { ComponentProps, CSSProperties, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChannelFilters } from "stream-chat";
import { useChatContext } from "stream-chat-react";
import { DefaultStreamChatGenerics } from "stream-chat-react/dist/types/types";
import { Channel as ChatChannel } from "./Channel";
import { ChannelList } from "./ChannelList";
import { useGetEntitiesQuery } from "store/apis/group";
import { setChannelInfoSliderActive } from "store/slices/channelInfoSlider";
import { RootState } from "store/store";

type MessageWidthVars = CSSProperties & Record<`--${string}`, string>;

export const Chat = ({
  filters,
  messageMaxWidth: defaultMessageMaxWidth = BIG,
  unreplied,
  broadcast,
  channelId,
  onChannelSelect,
  messageId,
  hideTabBar,
  hideFilters,
  channelHeaderProps,
  sortByNewest,
  isMyMessages,
  showEntityFilters,
}: {
  filters?: ChannelFilters<DefaultStreamChatGenerics>;
  messageMaxWidth?: string;
  unreplied?: boolean;
  broadcast?: boolean;
  channelId?: string;
  onChannelSelect?: (channelId: string, messageId?: string) => void;
  messageId?: string;
  hideTabBar?: boolean;
  channelHeaderProps?: ComponentProps<typeof ChatChannel>;
  sortByNewest?: boolean;
  isMyMessages?: boolean;
  showEntityFilters?: boolean;
} & Pick<ComponentProps<typeof ChannelListFilter>, "hideFilters">) => {
  const dispatch = useDispatch();
  const { client } = useChatContext();
  const isSmallScreen = useIsSmallScreen();
  const isChatExpanded = useSelector(
    (state: RootState) => state.chatExpanded.isExpanded
  );
  const chatPaneRef = useExpandedMessageWidth<HTMLDivElement>(isChatExpanded);
  const messageMaxWidth = isChatExpanded ? EXPANDED : defaultMessageMaxWidth;
  const messageWidthVars: MessageWidthVars = {
    "--message-max-width": messageMaxWidth,
  };

  const { data: { groups: [group] = [null] } = {} } = useGetEntitiesQuery();

  useEffect(() => {
    return () => {
      dispatch(setChannelInfoSliderActive({ isActive: false }));
    };
  }, [dispatch]);

  if (!client) {
    return <Loader />;
  }

  if (!client.user) {
    return <Loader />;
  }

  return (
    <>
      {!isSmallScreen && (
        <div
          className="t-flex-1 t-basis-auto t-overflow-hidden t-h-full"
          style={messageWidthVars}
        >
          <div className="t-block t-h-full md:t-flex">
            <div
              className={classNames(
                "t-w-full t-h-full t-border-solid t-border-r t-border-0 t-border-neutral-10",
                {
                  "md:t-w-4/12": !isChatExpanded,
                  "md:t-w-96 md:t-shrink-0": isChatExpanded,
                }
              )}
            >
              <ChannelList
                channelId={channelId}
                onSelect={onChannelSelect}
                filters={filters}
                unreplied={unreplied}
                broadcast={broadcast}
                hideTabBar={hideTabBar}
                hideFilters={hideFilters}
                sortByNewest={sortByNewest}
                isMyMessages={isMyMessages}
                showEntityFilters={showEntityFilters}
              />
            </div>

            <div
              ref={chatPaneRef}
              className={classNames("t-w-full t-shrink", {
                "md:t-w-8/12": !isChatExpanded,
                "md:t-flex-1 md:t-min-w-0": isChatExpanded,
              })}
            >
              <ChatChannel
                {...channelHeaderProps}
                messageId={messageId}
                channelId={channelId}
                messageMaxWidth={messageMaxWidth}
              >
                <ChannelMessageSearch onSelect={onChannelSelect} />
                <ChannelInfoSlider />
                <ChannelPendingActions />
                <ChannelPinnedItem />
                <ChannelMediaSlider />
                <WhatsappForwardedSlider />
                <MessageInfoSlider />
                <ChannelForwardChannelSelector />
                <ChatTicketSlider />
                <ChatActionCentreSlider />
              </ChatChannel>
            </div>
          </div>
        </div>
      )}
      {isSmallScreen && (
        <div
          className="t-flex-1 t-basis-auto t-overflow-hidden t-h-full"
          style={messageWidthVars}
        >
          <div className="t-block t-h-full md:t-flex">
            <div
              className={classNames("t-w-full t-h-full", {
                "t-block": channelId,
                "t-hidden": !channelId,
              })}
            >
              <ChatChannel
                {...channelHeaderProps}
                messageId={messageId}
                channelId={channelId}
                messageMaxWidth={messageMaxWidth}
              >
                <ChannelMessageSearch onSelect={onChannelSelect} />
                <ChannelInfoSlider />
                <ChannelPendingActions />
                <ChannelPinnedItem />
                <ChannelMediaSlider />
                <WhatsappForwardedSlider />
                <MessageInfoSlider />
                <ChannelForwardChannelSelector />
                <ChatTicketSlider />
                <ChatActionCentreSlider />
              </ChatChannel>
            </div>

            <div
              className={classNames(
                "t-w-full t-h-full t-border-solid t-border-r t-border-0 t-border-neutral-10",
                {
                  "t-block": !channelId,
                  "t-hidden": channelId,
                }
              )}
            >
              <ChannelList
                channelId={channelId}
                onSelect={onChannelSelect}
                filters={filters}
                unreplied={unreplied}
                broadcast={broadcast}
                hideTabBar={hideTabBar}
                hideFilters={hideFilters}
                isMyMessages={isMyMessages}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
